import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import './SignInPopup.css';

interface SignInPopupProps {
    isOpen: boolean;
    onClose: () => void;
    redirectPath?: string;
}

const SignInPopup: React.FC<SignInPopupProps> = ({ isOpen, onClose, redirectPath }) => {
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const googleButtonRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [isGoogleReady, setIsGoogleReady] = useState(false);

    useEffect(() => {
        const checkGoogle = () => {
            if (window.google) {
                setIsGoogleReady(true);
            }
        };

        checkGoogle();

        // If not immediately available, poll for it
        const interval = setInterval(checkGoogle, 500);
        return () => clearInterval(interval);
    }, []);

    const { login, loginWithGoogle, showGoogleSetupModal } = useAuth();

    const { initializeGoogleAuth, renderGoogleButton, signInWithGoogle } = useGoogleAuth({
        onSuccess: async (credential: string) => {
            setError('');
            setIsLoading(true);
            try {
                const result = await loginWithGoogle(credential);
                if (result.requiresSetup && result.setupData) {
                    showGoogleSetupModal(result.setupData);
                    handleClose();
                } else {
                    handleClose();
                    navigate(redirectPath || '/profile');
                }
            } catch (err: any) {
                setError(err.message || 'Google authentication failed');
            } finally {
                setIsLoading(false);
            }
        },
        onError: (error: any) => {
            setError(error.message || 'Google authentication failed');
        },
    });


    useEffect(() => {
        if (isOpen && isGoogleReady) {
            initializeGoogleAuth();

            // Small delay to ensure the element is rendered and ready
            const timer = setTimeout(() => {
                if (googleButtonRef.current) {
                    renderGoogleButton(googleButtonRef.current, 'outline', isDarkMode);
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [isOpen, isGoogleReady, initializeGoogleAuth, renderGoogleButton, isDarkMode]);

    // Re-render Google button when dark mode changes
    useEffect(() => {
        if (isOpen && isGoogleReady && googleButtonRef.current) {
            renderGoogleButton(googleButtonRef.current, 'outline', isDarkMode);
        }

    }, [isDarkMode, isOpen, isGoogleReady, renderGoogleButton]);

    // Close popup when clicking/tapping outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            // Handle both mouse and touch events for mobile
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const handleClose = () => {
        setEmail('');
        setPassword('');
        setError('');
        setIsLoading(false);
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent | React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();

        // Manual validation since we're not using a form
        if (!email || !email.trim()) {
            setError('Email is required');
            return;
        }
        if (!password || !password.trim()) {
            setError('Password is required');
            return;
        }

        setError('');
        setIsLoading(true);

        try {
            await login(email, password);
            handleClose();
            navigate(redirectPath || '/profile');
        } catch (err: any) {
            setError(err.message || 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    // Helper to determine input type - always use "text" to avoid Safari password prompts
    // We use CSS -webkit-text-security: disc to mask the password visually
    const getPasswordInputType = () => {
        return "text";
    };

    return (
        <div className={`signin-popup-overlay ${isDarkMode ? 'dark-mode' : ''}`}>
            <div
                ref={popupRef}
                className={`signin-popup ${isDarkMode ? 'dark-mode' : ''}`}
            >
                <div className="popup-header">
                    <button className="popup-close" onClick={handleClose}>
                        ×
                    </button>
                </div>

                <div className="popup-content">
                    {error && (
                        <div className="signin-error-message">
                            {error}
                        </div>
                    )}

                    {/* Using div instead of form to prevent Safari password manager from triggering on every keystroke */}
                    <div className="signin-form">
                        <div className="form-group">
                            <input
                                type="email"
                                name="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                id="signin-email"
                                placeholder="Email"
                                autoComplete="off"
                                data-lpignore="true"
                                onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                            />
                        </div>

                        <div className="form-group password-input-wrapper">
                            {/* Visual placeholder overlay - Safari can't detect this */}
                            {password.length === 0 && (
                                <span className="visual-placeholder">Password</span>
                            )}
                            <input
                                type="text"
                                name="signin_secret_field"
                                value={showPassword ? password : '•'.repeat(password.length)}
                                onChange={(e) => {
                                    if (showPassword) {
                                        setPassword(e.target.value);
                                    } else {
                                        // Handle backspace and typing when masked
                                        const newLen = e.target.value.length;
                                        const oldLen = password.length;
                                        if (newLen < oldLen) {
                                            // Backspace - remove characters
                                            setPassword(password.slice(0, newLen));
                                        } else if (newLen > oldLen) {
                                            // New character typed - get the new characters
                                            const newChars = e.target.value.slice(oldLen);
                                            setPassword(password + newChars.replace(/•/g, ''));
                                        }
                                    }
                                }}
                                id="signin-secret"
                                autoComplete="off"
                                data-lpignore="true"
                                data-form-type="other"
                                onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                        <circle cx="12" cy="12" r="3"></circle>
                                    </svg>
                                )}
                            </button>
                        </div>

                        <div className="forgot-password-link-container">
                            <Link to="/forgot" className="forgot-password-link" onClick={handleClose}>
                                Forgot Password?
                            </Link>
                        </div>

                        <button
                            type="button"
                            className="signin-submit-btn"
                            disabled={isLoading}
                            onClick={handleSubmit}
                        >
                            {isLoading ? 'Signing In...' : 'Sign In'}
                        </button>
                    </div>

                    <div className="google-auth-container">
                        {isGoogleReady ? (
                            <div className="google-auth-btn-wrapper">
                                <div ref={googleButtonRef} className="google-auth-btn-element" />
                                <div className="google-auth-btn-overlay">
                                    {/* Google Icon SVG */}
                                    <svg className="google-icon" width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.64 9.2c0-.637-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
                                        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.715H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
                                        <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                                        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.159 6.656 3.58 9 3.58z" fill="#EA4335" />
                                    </svg>
                                    <span className="auth-btn-text">Sign in with Google</span>
                                </div>
                            </div>
                        ) : (
                            <div className="google-fallback-btn">
                                <p>Loading Google Sign-In...</p>
                            </div>
                        )}
                    </div>


                    <div className="popup-footer">
                        <p>
                            Don't have an account?{' '}
                            <Link to={`/signup?redirect=${encodeURIComponent(redirectPath || window.location.pathname)}`} className="signup-link" onClick={handleClose}>
                                Sign up
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignInPopup;
